import { useSession } from '@/auth/useSession';
import { FormModal } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { deleteUser, getUserDeleteEligibility, getUsers } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { z } from 'zod';

const formSchema = z.object({
  confirmed: z.boolean().default(false),
});

const IMPERSONATION_DELETE_BLOCK_MESSAGE = 'Delete user is not available while impersonating another user.';

export interface DeleteUserProps {
  trigger?: ReactNode;
  user: UserWithRolesAndProfiles;
}

export function DeleteUser({ trigger, user }: DeleteUserProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isImpersonating } = useSession();

  const { data: eligibility, isPending: isEligibilityPending } = useQuery({
    ...getUserDeleteEligibility(user.id),
    enabled: !isImpersonating,
  });

  const { mutateAsync: deleteUserMutate, isPending: isDeletePending } = useMutation({
    mutationFn: deleteUser,
  });

  const canDelete = !isImpersonating && (eligibility?.canDelete ?? false);
  const isPending = !isImpersonating && (isEligibilityPending || isDeletePending);

  const onSubmit = async ({
    formData,
    closeModal,
  }: {
    formData: z.infer<typeof formSchema>;
    closeModal: () => void;
  }) => {
    if (isImpersonating || !canDelete) {
      return;
    }

    if (!formData.confirmed) {
      toast({
        title: 'Please confirm that you understand this user will lose access before continuing.',
      });
      return;
    }

    try {
      await deleteUserMutate(user.id);
      closeModal();
      queryClient.invalidateQueries(getUsers());
      navigate({
        to: '/users',
        search: {
          pageIndex: 0,
          pageSize: 50,
        },
      });
    } catch (e) {
      const error = airServerErrorFrom(e);
      toast({
        variant: 'destructive',
        title: 'Failed to delete user',
        description: error?.userFacingMessage,
      });
    }
  };

  return (
    <FormModal
      modalTitle="Delete user"
      trigger={
        trigger ?? (
          <Button type="button" variant="destructive">
            Delete user
          </Button>
        )
      }
      onSubmit={onSubmit}
      dialogContentProps={{ className: 'max-w-md' }}
      submitButtonProps={{
        variant: 'destructive',
        children: 'Delete user',
        loading: isPending,
        disabled: isImpersonating || isEligibilityPending || !canDelete,
      }}
      formSchema={formSchema}
      confirmBtnLabel="Delete user"
      defaultValues={{ confirmed: false }}
    >
      {({ form }) => (
        <div className="my-6 space-y-4">
          {isImpersonating ? (
            <Alert variant="destructive">
              <AlertTitle>This user cannot be deleted</AlertTitle>
              <AlertDescription>{IMPERSONATION_DELETE_BLOCK_MESSAGE}</AlertDescription>
            </Alert>
          ) : isEligibilityPending ? (
            <DialogDescription>Checking whether this user can be deleted…</DialogDescription>
          ) : !canDelete && eligibility?.reasons.length ? (
            <Alert variant="destructive">
              <AlertTitle>This user cannot be deleted</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4 space-y-1">
                  {eligibility.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <DialogDescription>
              This action removes the user from all lists and revokes their login/SSO access. Their past sessions,
              feedback and payment history are kept, and their email and SSO login become available for a new account.
            </DialogDescription>
          )}
          {canDelete && (
            <FormCheckbox
              name="confirmed"
              label="I understand this user will lose access and be removed from all lists"
              form={form}
              horizontal
            />
          )}
        </div>
      )}
    </FormModal>
  );
}
